import { compact, isNumber, isString, upperCamelCase } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import {
  getScanTemplatePart,
  ScanTemplatePart,
  ScanTemplatePartContext,
  ScanTemplatePartWithExtendedContext,
  ScanTemplateRoot,
  ScanTemplateTask,
} from '@kaiizen/kai-scan-template-system';
import { ColumnDef, columnSizingFeature, flexRender, tableFeatures, useTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMemo, useRef, useState } from 'react';

import { KAI_SHIKI_ALWAYS_DARK_COLORS, KAI_SHIKI_LAYOUT, KAI_SHIKI_TYPOGRAPHY } from '@/Client/Styles/kaiShikiTokens';
import { CommonSegmentedToggle } from '@kaiizen/kai-shiki/react';
import { formatUsd } from '../../Presenters';
import { useCachedOrgProduct } from '../../State';
import { ScrollableTabBar } from '../../Widgets';
import { getScanTemplatePartFilterFunction } from './ScanTemplateFilterCache';

const getNumberOrUndefined = (value: any) => (isNumber(value) ? value : undefined);

const TABLE_FEATURES = tableFeatures({ columnSizingFeature });

const COLUMN_WIDTHS = {
  type: 100,
  name: 300,
  product: 150,
  price: 100,
  laborPrice: 100,
  quantity: 100,
};

const TABLE_CELL_STYLE = css`
  label: TABLE_CELL_STYLE;
  position: relative;
  ${KAI_SHIKI_TYPOGRAPHY.small};
`;

const TABLE_CELL_CONTENTS_STYLE = css`
  label: TABLE_CELL_CONTENTS_STYLE;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 11px ${KAI_SHIKI_LAYOUT.spacing1p5}px;
  ${KAI_SHIKI_TYPOGRAPHY.small};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TABLE_NAME_CELL_CONTENTS_STYLE = css`
  label: TABLE_NAME_CELL_CONTENTS_STYLE;
  position: absolute;
  display: flex;
  align-items: center;
  gap: 8px;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TABLE_CATEGORY_CELL_CONTENTS_STYLE = css`
  label: TABLE_CATEGORY_CELL_CONTENTS_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.tiny};
  text-transform: uppercase;
`;

const TYPE_DISPLAY_NAME = {
  scanTemplate: 'Root',
  groupTemplate: 'Group',
  taskTemplate: 'Task',
  optionTemplate: 'Option',
  lineItemTemplate: 'Line Item',
};

const PRODUCT_IMAGE_STYLE = css`
  label: PRODUCT_IMAGE_STYLE;
  width: 40px;
  height: 40px;
  object-fit: cover;
`;

const GROUP_HEADER_STYLE = css`
  label: GROUP_HEADER_STYLE;
  display: flex;
  height: 30px;
  align-items: center;
  ${KAI_SHIKI_TYPOGRAPHY.tinyBold};
  padding: 0 ${KAI_SHIKI_LAYOUT.spacing1p5}px;
  border-right: 1px solid var(--altBorderForDarkMode);
  color: var(--text2);
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TABLE_HEADER_STYLE = css`
  label: TABLE_HEADER_STYLE;
  display: grid;
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--background4);
  border-bottom: 1px solid var(--border3);
`;

const TABLE_CONTAINER_STYLE = css`
  label: TABLE_CONTAINER_STYLE;
  height: 100%;
  overflow-y: auto;
  overflow-x: visible;
  position: relative;
`;

const BOTTOM_PADDING_STYLE = css`
  label: BOTTOM_PADDING_STYLE;
  height: 80px;
  background: var(--background1);
`;

const TABLE_WRAPPER_STYLE = css`
  label: TABLE_WRAPPER_STYLE;
  flex: 1;
  overflow: hidden;
  position: relative;
  background: var(--background);
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const TAB_BAR_CONTAINER_STYLE = css`
  label: TAB_BAR_CONTAINER_STYLE;
  display: flex;
  align-items: center;
  height: 50px;
  gap: ${KAI_SHIKI_LAYOUT.spacing1 * 2}px;
  padding: 0 ${KAI_SHIKI_LAYOUT.spacing1}px 0 0;
  background: ${KAI_SHIKI_ALWAYS_DARK_COLORS.background.dark};
`;

const TAB_BAR_SEGMENTED_CONTROL_CONTAINER_STYLE = css`
  label: TAB_BAR_SEGMENTED_CONTROL_CONTAINER_STYLE;
  padding-top: 10px;
`;

const SEGMENTED_CONTROL_STYLE = css`
  label: SEGMENTED_CONTROL_STYLE;
  display: flex;
  align-items: center;
  gap: ${KAI_SHIKI_LAYOUT.spacing1}px;
`;

const SEGMENTED_CONTROL_LABEL_STYLE = css`
  label: SEGMENTED_CONTROL_LABEL_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.xSmall}
  color: ${KAI_SHIKI_ALWAYS_DARK_COLORS.text.white};
  user-select: none;
`;

const TABLE_CONTENT_WRAPPER_STYLE = css`
  label: TABLE_CONTENT_WRAPPER_STYLE;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const TABLE_ROW_STYLE = css`
  label: TABLE_ROW_STYLE;
  display: grid;
  position: absolute;
  left: 0;
  width: 100%;
  height: 40px;
  cursor: pointer;
  color: var(--text2);

  &:hover {
    color: var(--text0);
    background: var(--hoverBackground);
  }
`;

const TABLE_ROW_SELECTED_STYLE = css`
  label: TABLE_ROW_SELECTED_STYLE;
  color: var(--text0);
  background: var(--selectedBackground);
`;

const MaterialsCell = ({ lineItem }: { lineItem: ScanTemplatePart }) => {
  const [sku] = lineItem.skuIdOptions?.filter(isString) ?? [];
  const { orgProduct } = useCachedOrgProduct(sku);
  const price = orgProduct?.price || getNumberOrUndefined(lineItem.values?.price?.value);
  return <div css={TABLE_CELL_CONTENTS_STYLE}>{price ? formatUsd(price) : '-'}</div>;
};

/** Convert hierarchical task data into flat rows with full context */
const flattenTaskTemplatesToRows = (scanTemplateTasks: ScanTemplateTask[], filterRegex?: RegExp): ScanTemplatePartWithExtendedContext[] => {
  // const scanTemplateTasks = [...taskTemplates].sort((a, b) => a.name.localeCompare(b.name));
  const filterFunction = getScanTemplatePartFilterFunction(filterRegex);
  const flattenedRows: ScanTemplatePartWithExtendedContext[] = [];
  for (const scanTemplateTask of scanTemplateTasks) {
    let filteredOptionParts: ScanTemplatePartWithExtendedContext[] = [];
    scanTemplateTask.optionTemplates?.forEach(optionTemplate => {
      const filteredLineItems = optionTemplate.lineItemTemplates?.filter(filterFunction) ?? [];
      if (filteredLineItems.length > 0 || filterFunction(optionTemplate)) {
        filteredOptionParts.push({
          type: 'optionTemplate',
          scanTemplatePart: optionTemplate,
          taskId: scanTemplateTask.id,
          optionId: optionTemplate.id,
        });
        filteredLineItems.forEach(lineItem => {
          filteredOptionParts.push({
            type: 'lineItemTemplate',
            scanTemplatePart: lineItem,
            taskId: scanTemplateTask.id,
            optionId: optionTemplate.id,
            lineItemId: lineItem.id,
          });
        });
      }
    });
    if (filteredOptionParts.length > 0 || filterFunction(scanTemplateTask)) {
      flattenedRows.push({
        type: 'taskTemplate',
        scanTemplatePart: scanTemplateTask,
        taskId: scanTemplateTask.id,
      });
      flattenedRows.push(...filteredOptionParts);
    }
  }
  return flattenedRows;
};

const OrgProductName = ({ sku }: { sku: string }) => {
  const { orgProduct } = useCachedOrgProduct(sku);
  if (!orgProduct) return '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {orgProduct.image ? <img src={orgProduct.image} alt={orgProduct.title} css={PRODUCT_IMAGE_STYLE} /> : <div style={{ width: '40px', height: '40px' }} />}
      <span>{orgProduct.title}</span>
    </div>
  );
};

const OrgProductDescription = ({ sku }: { sku: string }) => {
  const { orgProduct } = useCachedOrgProduct(sku);
  return orgProduct ? orgProduct.description : '';
};

const OrgProductDisplayedSku = ({ sku }: { sku: string | undefined }) => {
  const { orgProduct } = useCachedOrgProduct(sku);
  return orgProduct?.thdStoreSku ?? orgProduct?.sku ?? '';
};

const createColumnDefinitions = (): ColumnDef<typeof TABLE_FEATURES, ScanTemplatePartWithExtendedContext>[] => [
  {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => <div css={TABLE_CELL_CONTENTS_STYLE}>{TYPE_DISPLAY_NAME[row.original.type]}</div>,
    size: COLUMN_WIDTHS.type,
  },
  {
    id: 'name',
    header: 'Name',
    cell: ({ row }) => {
      const taskRow = row.original;
      const skuIdOption = row.original.scanTemplatePart?.skuIdOptions?.[0] as string | undefined;

      let name = taskRow.scanTemplatePart.name;
      const hierarchyIndentationLevel = taskRow.type === 'taskTemplate' || skuIdOption ? 0 : taskRow.type === 'optionTemplate' ? 25 : 50;
      return (
        <div css={TABLE_NAME_CELL_CONTENTS_STYLE} style={{ paddingLeft: hierarchyIndentationLevel }}>
          {(taskRow.scanTemplatePart.trade || taskRow.scanTemplatePart.category) && (
            <div css={TABLE_CATEGORY_CELL_CONTENTS_STYLE}>
              {compact([
                taskRow.scanTemplatePart.trade,
                taskRow.scanTemplatePart.category && taskRow.scanTemplatePart.category !== taskRow.scanTemplatePart.trade
                  ? taskRow.scanTemplatePart.category
                  : undefined,
              ])
                .filter(Boolean)
                .join(' / ')}
              :
            </div>
          )}
          {skuIdOption ? <OrgProductName sku={skuIdOption} /> : name || ''}
        </div>
      );
    },
    size: COLUMN_WIDTHS.name,
  },
  {
    id: 'product',
    header: 'SKU',
    cell: ({ row }) => {
      if (row.original.type !== 'lineItemTemplate') return <div css={TABLE_CELL_CONTENTS_STYLE}></div>;
      const skuIdOption = row.original.scanTemplatePart?.skuIdOptions?.[0] as string | undefined;
      return (
        <div css={TABLE_CELL_CONTENTS_STYLE}>
          <OrgProductDisplayedSku sku={skuIdOption} />
        </div>
      );
    },
    size: COLUMN_WIDTHS.product,
  },
  {
    id: 'price',
    header: 'Materials',
    cell: ({ row }) => {
      if (row.original.type !== 'lineItemTemplate') return <div css={TABLE_CELL_CONTENTS_STYLE}></div>;
      return <MaterialsCell lineItem={row.original.scanTemplatePart} />;
    },
    size: COLUMN_WIDTHS.price,
  },
  {
    id: 'laborPrice',
    header: 'Labor',
    cell: ({ row }) => {
      if (row.original.type !== 'lineItemTemplate') return <div css={TABLE_CELL_CONTENTS_STYLE}></div>;
      const lineItem = row.original.scanTemplatePart;
      const laborPrice = getNumberOrUndefined(lineItem.values?.laborPrice?.value);
      return <div css={TABLE_CELL_CONTENTS_STYLE}>{laborPrice ? formatUsd(laborPrice) : '-'}</div>;
    },
    size: COLUMN_WIDTHS.laborPrice,
  },
  {
    id: 'quantity',
    header: 'Default Qty',
    cell: ({ row }) => {
      if (row.original.type !== 'lineItemTemplate') return <div css={TABLE_CELL_CONTENTS_STYLE}></div>;
      const lineItem = row.original.scanTemplatePart;
      const quantity = lineItem.values?.quantity ? `${lineItem.values.quantity.value} ${lineItem.values.quantity.type}` : '-';
      return <div css={TABLE_CELL_CONTENTS_STYLE}>{quantity}</div>;
    },
    size: COLUMN_WIDTHS.quantity,
  },
  {
    id: 'description',
    header: 'Description',
    cell: ({ row }) => {
      const taskRow = row.original;
      const skuIdOption = row.original.scanTemplatePart?.skuIdOptions?.[0] as string | undefined;
      const description = skuIdOption ? <OrgProductDescription sku={skuIdOption} /> : taskRow.scanTemplatePart.description;
      return <div css={TABLE_CELL_CONTENTS_STYLE}>{description || ''}</div>;
    },
    // No fixed size - will use 1fr in grid to fill remaining space
  },
];

type GroupingMode = 'category' | 'trade';

type GroupingValueWithCount = {
  value: string;
  count: number;
};

const extractUniqueFieldValuesWithCounts = (tasks: ScanTemplateTask[], field: 'category' | 'trade'): GroupingValueWithCount[] => {
  const valueCounts = new Map<string, number>();
  tasks.forEach(task => {
    const value = task[field];
    if (value) {
      valueCounts.set(value, (valueCounts.get(value) || 0) + 1);
    }
  });
  return Array.from(valueCounts.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => a.value.localeCompare(b.value));
};

const filterTasksByField = (tasks: ScanTemplateTask[], field: 'category' | 'trade', value: string): ScanTemplateTask[] =>
  tasks.filter(task => task[field] === value);

const GROUPING_MODE_OPTIONS: { value: GroupingMode; label: string }[] = [
  { value: 'category', label: 'Category' },
  { value: 'trade', label: 'Trade' },
];

export const TemplateTasksEditor = ({
  groupId,
  tasks,
  templateRoot,
  selectedScanTemplatePartContext,
  setSelectedScanTemplatePartContext,
  filterRegex,
}: {
  groupId?: string;
  tasks: ScanTemplateTask[];
  templateRoot: ScanTemplateRoot;
  selectedScanTemplatePartContext: ScanTemplatePartContext | null;
  setSelectedScanTemplatePartContext: (context: ScanTemplatePartContext | null) => void;
  filterRegex?: RegExp;
}) => {
  const selectedScanPart = selectedScanTemplatePartContext ? getScanTemplatePart(templateRoot, selectedScanTemplatePartContext) : null;

  const [groupingMode, setGroupingMode] = useState<GroupingMode>('category');
  const [selectedTab, setSelectedTab] = useState<string>('all');

  const groupingValuesWithCounts = useMemo(() => extractUniqueFieldValuesWithCounts(tasks, groupingMode), [tasks, groupingMode]);

  const tabItems = useMemo(
    () => [
      { label: `All Tasks (${tasks.length})`, value: 'all' },
      ...groupingValuesWithCounts.map(({ value, count }) => ({
        label: `${upperCamelCase(value, ' ')} Tasks (${count})`,
        value,
      })),
    ],
    [tasks.length, groupingValuesWithCounts],
  );

  const filteredTasks = useMemo(() => {
    const filtered = selectedTab === 'all' ? tasks : filterTasksByField(tasks, groupingMode, selectedTab);
    return [...filtered].sort((a, b) => {
      const categoryA = a.category || '';
      const categoryB = b.category || '';
      return categoryA.localeCompare(categoryB);
    });
  }, [tasks, selectedTab, groupingMode]);

  const flattenedRows = useMemo(() => flattenTaskTemplatesToRows(filteredTasks, filterRegex), [filteredTasks, filterRegex]);

  const columnDefinitions = useMemo(() => createColumnDefinitions(), []);

  const tableInstance = useTable({
    features: TABLE_FEATURES,
    data: flattenedRows,
    columns: columnDefinitions,
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: tableInstance.getRowModel().rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 40,
    overscan: 10,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const GRID_TEMPLATE = columnDefinitions.map(column => (column.size ? `${column.size}px` : '1fr')).join(' ');

  return (
    <div css={TABLE_WRAPPER_STYLE}>
      {/* Tab Bar with Toggle */}
      <div css={TAB_BAR_CONTAINER_STYLE}>
        <div css={TAB_BAR_SEGMENTED_CONTROL_CONTAINER_STYLE}>
          <div css={SEGMENTED_CONTROL_STYLE} onClickCapture={() => setSelectedTab('all')}>
            <div css={SEGMENTED_CONTROL_LABEL_STYLE}>View By</div>
            <CommonSegmentedToggle
              value={groupingMode}
              options={GROUPING_MODE_OPTIONS}
              onChange={value => {
                setGroupingMode(value);
              }}
            />
          </div>
        </div>
        <ScrollableTabBar items={tabItems} value={selectedTab} onChange={setSelectedTab} />
      </div>

      {/* Table Content */}
      <div css={TABLE_CONTENT_WRAPPER_STYLE}>
        {/* Header */}
        <div
          css={[
            TABLE_HEADER_STYLE,
            {
              gridTemplateColumns: GRID_TEMPLATE,
            },
          ]}
        >
          {tableInstance.getHeaderGroups().map(headerGroup =>
            headerGroup.headers.map(header => (
              <div key={header.id} css={GROUP_HEADER_STYLE}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </div>
            ))
          )}
        </div>

        {/* Virtualized rows container */}
        <div ref={scrollContainerRef} css={TABLE_CONTAINER_STYLE}>
          <div style={{ position: 'relative', height: totalHeight + 80 }}>
            {virtualRows.map(virtualRow => {
              const tableRow = tableInstance.getRowModel().rows[virtualRow.index];
              const isTaskRow = tableRow.original.type === 'taskTemplate';
              const isOptionRow = tableRow.original.type === 'optionTemplate';
              const isSelectedRow = selectedScanPart?.id === tableRow.original.scanTemplatePart.id;
              return (
                <div
                  key={tableRow.original.scanTemplatePart.id}
                  onClick={() =>
                    setSelectedScanTemplatePartContext({
                      groupId: groupId ?? tableRow.original.groupId,
                      taskId: tableRow.original.taskId,
                      optionId: tableRow.original.optionId,
                      lineItemId: tableRow.original.lineItemId,
                    })}
                  css={[
                    TABLE_ROW_STYLE,
                    isSelectedRow ? TABLE_ROW_SELECTED_STYLE : '',
                    {
                      background: isTaskRow ? 'var(--background3)' : isOptionRow ? 'var(--background2)' : undefined,
                      top: virtualRow.start,
                      gridTemplateColumns: GRID_TEMPLATE,
                    },
                  ]}
                >
                  {tableRow.getAllCells().map(tableCell => (
                    <div
                      key={tableCell.id}
                      css={[
                        TABLE_CELL_STYLE,
                        {
                          borderRight: isTaskRow ? 'none' : '1px solid var(--background4)',
                        },
                      ]}
                    >
                      {flexRender(tableCell.column.columnDef.cell, tableCell.getContext())}
                    </div>
                  ))}
                </div>
              );
            })}
            {/* Bottom padding to prevent hover overlay interference */}
            <div css={BOTTOM_PADDING_STYLE} />
          </div>
        </div>
      </div>
    </div>
  );
};
